/**
 * WebSocket 서비스 구현체
 * WebSocket 서버와 연동하여 채널 구독 기능 제공
 */

import type {
  IWebSocketService,
  WebSocketCallback,
  ConnectionState,
  WebSocketChannel,
  WebSocketSubscriptionMessage,
  DecodedMarketQuote,
  DecodedMarketQuoteList,
  DecodedMarketOrderBook,
  DecodedMarketTicker,
  DecodedOrderReceived,
  DecodedOrderRejected,
  DecodedOrderExecuted,
  DecodedBalanceUpdated,
  DecodedDepositUpdated,
  DecodedOrderClosed,
  IOrderBookCancel,
} from '@template/types';
import {
  useMarketDataStore,
  useMetadataStore,
  useAccountStore,
  useAssetStore,
  useOrderDataStore,
  useToastStore,
} from '@template/stores';
import { globalEventBus, MARKET_EVENTS, ReconnectManager } from '@template/utils';
import { toDecimalString, parseDateTimeString, subtract } from '@template/utils';
import { useSelectedSymbolStore } from '@/stores/useSelectedSymbolStore';
import { authService, accountService } from '@/services/api';
import { BinaryMessageDecoder } from '@template/types';
import { EnumLabelMapper } from '@template/api';
import { WEBSOCKET_CONFIG } from './index';

const SOCKET_CLOSE_WAIT_TIMEOUT_MS = 10000;

/**
 * WebSocket 서비스 클래스
 */
interface ChannelSubscriptionOptions {
  stockCd?: string;
}

export class WebSocketService implements IWebSocketService {
  private connectionState: ConnectionState = 'disconnected';
  private subscriptions: Map<string, WebSocketCallback> = new Map();
  private webSocket: WebSocket | null = null;
  private subscribedChannels: Map<WebSocketChannel, ChannelSubscriptionOptions | undefined> =
    new Map();
  private accessToken: string | null = null;
  // connect()가 동시에 여러 번 호출되어도, 진행 중인 연결은 하나만 유지
  private connectPromise: Promise<void> | null = null;
  // reconnect()도 동시 호출 시 하나의 작업만 진행
  private reconnectPromise: Promise<void> | null = null;
  private autoReconnect: boolean = true; // 자동 재연결 활성화 여부 (의도적 종료 시 false)
  private reconnectManager: ReconnectManager | null = null;
  // 연결이 바뀔 때마다 1씩 증가. 늦게 도착한 이전 소켓 이벤트는 무시할 때 사용
  private socketGeneration: number = 0;
  // TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
  private quoteLogIntervalId: NodeJS.Timeout | null = null;

  constructor() {
    // ReconnectManager는 lazy initialization (authService, accountService가 초기화된 후에 생성)
    // 개발 모드에서 window 객체에 메서드 노출
    this.exposeDevMethods();
  }

  /**
   * ReconnectManager 초기화 (lazy initialization)
   */
  private getReconnectManager(): ReconnectManager {
    if (!this.reconnectManager) {
      this.reconnectManager = new ReconnectManager(
        this,
        authService,
        accountService,
        () => useAccountStore(),
        {
          isEnabled: () => this.autoReconnect,
          backoff: WEBSOCKET_CONFIG.reconnect,
          onReconnected: () => {
            window.dispatchEvent(new CustomEvent('websocketReconnected'));
          },
        }
      );
    }
    return this.reconnectManager;
  }

  /**
   * 포지션 구분 코드를 매수/매도 라벨로 변환 (LONG → 매수, SHORT → 매도)
   * @param positionCd - 포지션 구분 코드 (LONG, SHORT 등)
   * @returns 매수 | 매도 | ''
   */
  private getSideLabelFromPositionCd(positionCd: string | undefined): string {
    return positionCd === 'LONG' ? '매수' : positionCd === 'SHORT' ? '매도' : '';
  }

  /**
   * WebSocket 연결 상태 확인
   */
  isConnected(): boolean {
    return this.connectionState === 'connected' && this.webSocket?.readyState === WebSocket.OPEN;
  }

  /**
   * WebSocket 연결 상태 조회
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * 액세스 토큰 설정 - 로그인 성공 후 호출
   */
  setAccessToken(token: string): void {
    this.accessToken = token;
  }

  /**
   * 액세스 토큰 제거 - 로그아웃 시 호출
   */
  clearAccessToken(): void {
    this.accessToken = null;
  }

  private isCurrentSocket(socket: WebSocket, generation: number): boolean {
    // 같은 소켓 인스턴스이고, 연결 시점 번호가 현재와 같을 때만 처리
    return this.webSocket === socket && this.socketGeneration === generation;
  }

  private clearSocketHandlers(socket: WebSocket): void {
    socket.onopen = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
  }

  private async closeSocketAndWait(
    socket: WebSocket,
    timeoutMs: number = SOCKET_CLOSE_WAIT_TIMEOUT_MS
  ): Promise<boolean> {
    // close 이벤트를 기다려 "완전히 닫힌 뒤 다음 동작"을 보장한다.
    // 반환값: true=정상 종료(또는 이미 CLOSED), false=타임아웃/예외
    if (socket.readyState === WebSocket.CLOSED) {
      return true;
    }

    return await new Promise<boolean>((resolve) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const finalize = (closed: boolean) => {
        if (settled) {
          return;
        }
        settled = true;
        socket.removeEventListener('close', onClose);
        socket.removeEventListener('error', onError);
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        resolve(closed);
      };

      const onClose = () => finalize(true);
      const onError = () => finalize(true);

      socket.addEventListener('close', onClose);
      socket.addEventListener('error', onError);

      timeoutId = setTimeout(() => finalize(false), timeoutMs);

      try {
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close();
        }
      } catch (error) {
        console.warn('[WebSocketService] 소켓 종료 중 예외 발생:', error);
        finalize(false);
      }
    });
  }

  /**
   * WebSocket 연결
   */
  async connect(): Promise<void> {
    if (this.isConnected()) {
      return;
    }

    // 이미 진행 중인 connect가 있으면 같은 Promise를 반환해 중복 연결을 방지한다.
    if (this.connectionState === 'connecting' && this.connectPromise) {
      return this.connectPromise;
    }
    // 아래 await 전에 '연결 중'으로 표시해, 동시 connect()가 새 연결을 또 만들지 않게 함
    this.connectionState = 'connecting';
    this.connectPromise = (async () => {
      const existingSocket = this.webSocket;
      if (
        existingSocket &&
        (existingSocket.readyState === WebSocket.OPEN ||
          existingSocket.readyState === WebSocket.CONNECTING ||
          existingSocket.readyState === WebSocket.CLOSING)
      ) {
        // 닫는 동안 이전 소켓 이벤트가 들어와도 무시되도록 번호를 먼저 올림
        this.socketGeneration += 1;
        this.webSocket = null;
        this.clearSocketHandlers(existingSocket);
        const isClosed = await this.closeSocketAndWait(existingSocket);
        if (!isClosed) {
          this.connectionState = 'error';
          throw new Error('[WebSocketService] 기존 WebSocket 종료 타임아웃으로 연결을 중단합니다.');
        }
      }

      // 이번 연결에 부여한 번호. 이후 이벤트가 오래된 연결에서 온 것인지 판별할 때 사용
      const generation = ++this.socketGeneration;

      return new Promise<void>((resolve, reject) => {
      let isSettled = false;
      // 연결 타임아웃(10초). 성공/실패 시 clearTimeout 하려고 바깥에 둠
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      // Promise는 한 번만 완료. onopen·onerror·타임아웃이 겹쳐 호출돼도 첫 호출만 반영
      const resolveOnce = () => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        resolve();
      };

      // resolveOnce와 동일. 실패 시에도 중복 reject·타임아웃 정리 방지
      const rejectOnce = (error: unknown) => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        reject(error);
      };

      try {
        const socket = new WebSocket(WEBSOCKET_CONFIG.url);
        this.webSocket = socket;

        socket.onopen = () => {
          if (!this.isCurrentSocket(socket, generation)) {
            // 이미 교체된 이전 소켓의 늦은 onopen 이벤트는 무시
            return;
          }
          this.connectionState = 'connected';
          this.connectPromise = null;
          this.getReconnectManager().cancelReconnect();

          // 재연결인 경우에만 저장된 채널 목록 재구독
          if (this.subscribedChannels.size > 0) {
            this.resubscribeStoredChannels();
          }

          // TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
          this.startPeriodicQuoteLog();

          resolveOnce();
        };

        socket.onclose = () => {
          if (!this.isCurrentSocket(socket, generation)) {
            // 이미 교체된 이전 소켓의 늦은 onclose 이벤트는 무시
            return;
          }
          if (this.isQuoteDebugLogEnabled()) {
            console.warn('[DEBUG] WebSocket 연결 해제');
          }
          this.connectionState = 'disconnected';
          this.connectPromise = null;
          this.webSocket = null;

          // TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
          this.stopPeriodicQuoteLog();

          // autoReconnect 설정에 따라 재연결 또는 정리
          if (this.autoReconnect) {
            // 자동 재연결 활성화: initialDelay 후 재연결 시도
            this.getReconnectManager().scheduleReconnect();
          } else {
            // 의도적 종료: 채널 목록 정리 (재연결하지 않음)
            this.subscribedChannels.clear();
          }
        };

        socket.onerror = (error) => {
          if (!this.isCurrentSocket(socket, generation)) {
            // 이미 교체된 이전 소켓의 늦은 onerror 이벤트는 무시
            return;
          }
          this.connectionState = 'error';
          this.connectPromise = null;
          console.error('[WebSocketService] WebSocket 오류:', error);
          rejectOnce(error);
        };

        socket.onmessage = async (event) => {
          if (!this.isCurrentSocket(socket, generation)) {
            // 이미 교체된 이전 소켓의 늦은 메시지는 처리하지 않는다.
            return;
          }
          try {
            // 바이너리 메시지만 처리
            if (event.data instanceof ArrayBuffer || event.data instanceof Blob) {
              await this.handleBinaryMessage(event.data);
            } else {
              console.warn('[WebSocketService] 바이너리가 아닌 메시지 수신:', typeof event.data);
            }
          } catch (error) {
            console.error(
              '[WebSocketService] 바이너리 메시지 처리 오류:',
              error,
              '원시 데이터:',
              event.data
            );
          }
        };

        timeoutId = setTimeout(async () => {
          if (this.isCurrentSocket(socket, generation) && this.connectionState === 'connecting') {
            this.connectionState = 'error';
            this.connectPromise = null;
            this.webSocket = null;
            this.clearSocketHandlers(socket);
            await this.closeSocketAndWait(socket, 1000);
            rejectOnce(new Error('WebSocket 연결 타임아웃'));
          }
        }, 10000); // 10초 타임아웃
      } catch (error) {
        this.connectionState = 'error';
        this.connectPromise = null;
        rejectOnce(error);
      }
      });
    })();

    return this.connectPromise;
  }

  /**
   * WebSocket 연결 해제
   *
   * @param allowAutoReconnect - 자동 재연결 허용 여부 (기본값: true, 자동 재연결 허용)
   *                             allowAutoReconnect=true인 경우, ReconnectManager에서 Access Token을 확인하여 재연결 여부를 결정합니다.
   */
  async disconnect(allowAutoReconnect: boolean = true): Promise<void> {
    // 자동 재연결 설정 (disconnect 경로에서 재연결 스케줄 여부를 결정)
    this.autoReconnect = allowAutoReconnect;
    if (this.reconnectManager) {
      this.reconnectManager.setAutoReconnect(allowAutoReconnect);
      // 진행 중인 재연결 시도 취소
      this.reconnectManager.cancelReconnect();
    }

    // TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
    this.stopPeriodicQuoteLog();

    // WebSocket 종료 (disconnect 경로에서 재연결 또는 정리 처리)
    const socket = this.webSocket;
    if (!socket) {
      // 풀싱크(disconnect false)는 진행 중 connect도 무효화해야 attemptReconnect가 스킵되지 않는다.
      if (this.connectionState === 'connecting' && this.connectPromise && allowAutoReconnect) {
        return;
      }
      this.socketGeneration += 1;
      this.webSocket = null;
      this.connectionState = 'disconnected';
      this.connectPromise = null;
      if (allowAutoReconnect) {
        this.getReconnectManager().scheduleReconnect();
      } else {
        this.subscribedChannels.clear();
      }
      return;
    }

    this.socketGeneration += 1;
    this.webSocket = null;
    this.connectPromise = null;
    this.connectionState = 'disconnected';
    this.clearSocketHandlers(socket);

    const isClosed = await this.closeSocketAndWait(socket);
    if (!isClosed) {
      console.warn('[WebSocketService] disconnect 대기 중 close 타임아웃 발생');
    }

    if (allowAutoReconnect) {
      // disconnect에서는 핸들러를 제거하므로 onclose에 의존하지 않고 명시적으로 재연결 예약
      this.getReconnectManager().scheduleReconnect();
    } else {
      this.subscribedChannels.clear();
    }
  }

  /**
   * 채널 구독 (채널명 직접 지정)
   */
  subscribe(channel: WebSocketChannel, callback: WebSocketCallback): string {
    const subscriptionId = `${channel}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.subscriptions.set(subscriptionId, callback);

    // WebSocket으로 구독 메시지 전송
    if (this.isConnected()) {
      this.subscribeToChannels([channel]);
    }

    return subscriptionId;
  }

  /**
   * 채널 구독
   *
   * 역할: WebSocket 서버로 구독 메시지를 전송하는 것만 담당합니다.
   * 어떤 채널을 언제 구독할지는 useWebSocketSubscription에서 결정합니다.
   *
   * @param channels - 구독할 채널 배열
   * @param options - 구독 옵션 (stockCd, token 등)
   * @param isResubscribe - 재구독 여부 (기본값: false, 재구독 시 true)
   */
  subscribeToChannels(
    channels: WebSocketChannel[],
    options?: { stockCd?: string; token?: string },
    isResubscribe: boolean = false
  ): void {
    if (!this.isConnected()) {
      console.warn('[WebSocketService] WebSocket 연결 없음 - 채널 구독 실패');
      return;
    }

    if (channels.length === 0) {
      return;
    }

    // 재구독이 아닌 경우에만 이미 구독 중인 채널 제외
    const newChannels = isResubscribe
      ? channels
      : channels.filter((channel) => !this.subscribedChannels.has(channel));

    if (newChannels.length === 0) {
      return;
    }

    // market.orderbook 또는 market.orderbook.cancel 채널이 있으면 options.stockCd 포함
    const hasMarketOrderBookChannel = newChannels.some(
      (channel) => channel === 'market.orderbook' || channel === 'market.orderbook.cancel'
    );

    // 토큰은 options에서 전달받거나, 없으면 내부 토큰 사용
    const token = options?.token || (this.accessToken ? `Bearer ${this.accessToken}` : undefined);

    const message: WebSocketSubscriptionMessage = {
      action: 'SUBSCRIBE',
      channels: newChannels,
      ...(token && { token }),
      ...(hasMarketOrderBookChannel &&
        options?.stockCd && { options: { stockCd: options.stockCd } }),
    };

    this.webSocket!.send(JSON.stringify(message));

    // 구독된 채널 목록에 추가 (옵션 포함)
    const channelOptions: ChannelSubscriptionOptions | undefined = options?.stockCd
      ? { stockCd: options.stockCd }
      : undefined;
    newChannels.forEach((channel) => {
      this.subscribedChannels.set(channel, channelOptions);
    });
  }

  /**
   * 채널 구독 해제
   *
   * 역할: WebSocket 서버로 구독 해제 메시지를 전송하는 것만 담당합니다.
   * 어떤 채널을 언제 해제할지는 useWebSocketSubscription에서 결정합니다.
   *
   * @param channels - 구독 해제할 채널 배열
   * @param options - 구독 해제 옵션 (token, stockCd 등)
   */
  unsubscribeFromChannels(
    channels: WebSocketChannel[],
    options?: { token?: string; stockCd?: string }
  ): void {
    if (!this.isConnected()) {
      console.warn('[WebSocketService] WebSocket 연결 없음 - 채널 구독 해제 실패');
      return;
    }

    const channelsToUnsubscribe = channels.filter((channel) =>
      this.subscribedChannels.has(channel)
    );
    if (channelsToUnsubscribe.length === 0) {
      return;
    }

    // 토큰은 options에서 전달받거나, 없으면 내부 토큰 사용
    const token = options?.token || (this.accessToken ? `Bearer ${this.accessToken}` : undefined);

    // market.orderbook 또는 market.orderbook.cancel 채널이 있으면 stockCd 포함
    const hasMarketOrderBookChannel = channelsToUnsubscribe.some(
      (channel) => channel === 'market.orderbook' || channel === 'market.orderbook.cancel'
    );

    // stockCd는 options에서 전달받거나, 저장된 옵션에서 가져오기
    let stockCd: string | undefined = options?.stockCd;
    if (hasMarketOrderBookChannel && !stockCd) {
      const orderBookChannel = channelsToUnsubscribe.find(
        (channel) => channel === 'market.orderbook' || channel === 'market.orderbook.cancel'
      );
      if (orderBookChannel) {
        stockCd = this.subscribedChannels.get(orderBookChannel)?.stockCd;
      }
    }

    const message: WebSocketSubscriptionMessage = {
      action: 'UNSUBSCRIBE',
      channels: channelsToUnsubscribe,
      ...(token && { token }),
      ...(hasMarketOrderBookChannel && stockCd && { options: { stockCd } }),
    };

    this.webSocket!.send(JSON.stringify(message));

    // 구독된 채널 목록에서 제거
    channelsToUnsubscribe.forEach((channel) => this.subscribedChannels.delete(channel));
  }

  /**
   * 채널 구독 해제 후 즉시 재구독 (한 번에 처리)
   * 계좌 변경 시 JWT 토큰이 변경되어 재구독이 필요할 때 사용합니다.
   *
   * @param channelsToUnsubscribe - 구독 해제할 채널 배열
   * @param channelsToSubscribe - 재구독할 채널 배열
   * @param options - 구독 옵션 (stockCd, token 등)
   */
  resubscribeChannels(
    channelsToUnsubscribe: WebSocketChannel[],
    channelsToSubscribe: WebSocketChannel[],
    options?: { stockCd?: string; token?: string }
  ): void {
    if (!this.isConnected()) {
      console.warn('[WebSocketService] WebSocket 연결 없음 - 채널 재구독 실패');
      return;
    }

    // 구독 해제할 채널 필터링
    const channelsToUnsub = channelsToUnsubscribe.filter((channel) =>
      this.subscribedChannels.has(channel)
    );

    // 구독 해제 메시지 전송
    if (channelsToUnsub.length > 0) {
      this.unsubscribeFromChannels(channelsToUnsub, { token: options?.token });
    }

    // 재구독 메시지 전송 (구독 해제 후 즉시)
    if (channelsToSubscribe.length > 0) {
      this.subscribeToChannels(channelsToSubscribe, {
        stockCd: options?.stockCd,
        token: options?.token,
      });
    }
  }

  /**
   * 구독 해제 (채널명 또는 ID 기반)
   */
  unsubscribe(channelOrId: string): void {
    // ID로 구독 해제 시도
    const callback = this.subscriptions.get(channelOrId);
    if (callback) {
      this.subscriptions.delete(channelOrId);
      // console.log(`[WebSocketService] 구독 해제: ${channelOrId}`);
      return;
    }

    // 채널명으로 구독 해제 시도
    const keysToDelete: string[] = [];
    this.subscriptions.forEach((cb, key) => {
      if (key.startsWith(channelOrId + '_')) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      this.subscriptions.delete(key);
    });

    // WebSocket으로 구독 해제 메시지 전송
    if (this.isConnected() && keysToDelete.length > 0) {
      this.unsubscribeFromChannels([channelOrId as WebSocketChannel]);
    }
  }

  /**
   * 개별 채널 구독 해제 (예제 방식)
   */
  unsubscribeChannel(channel: WebSocketChannel): void {
    if (!this.isConnected()) {
      console.warn('[WebSocketService] WebSocket 연결 없음 - 채널 구독 해제 실패');
      return;
    }

    if (!this.subscribedChannels.has(channel)) {
      return;
    }

    const message: WebSocketSubscriptionMessage = {
      action: 'UNSUBSCRIBE',
      channels: [channel],
      ...(this.accessToken && { token: `Bearer ${this.accessToken}` }),
    };

    this.webSocket!.send(JSON.stringify(message));
    // console.log(`[WebSocketService] 개별 채널 구독 해제: ${channel}`, message);

    // 구독된 채널 목록에서 제거
    this.subscribedChannels.delete(channel);

    // 해당 채널의 콜백들도 제거
    const keysToDelete: string[] = [];
    this.subscriptions.forEach((cb, key) => {
      if (key.startsWith(channel + '_')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => this.subscriptions.delete(key));
  }

  /**
   * 구독 해제 (심볼 기반)
   *
   * 주의: 이 메서드는 내부 콜백 구독만 해제하며, WebSocket 채널 구독은 해제하지 않습니다.
   * 채널 구독/해제는 useWebSocketSubscription에서 관리합니다.
   *
   * @param symbol - 구독 해제할 심볼
   * @param callback - 특정 콜백만 해제 (선택사항)
   */
  unsubscribeBySymbol(symbol: string, callback?: WebSocketCallback): void {
    const keysToDelete: string[] = [];

    this.subscriptions.forEach((cb, key) => {
      if (key.startsWith(symbol + '_') && (!callback || cb === callback)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      this.subscriptions.delete(key);
      // console.log(`[WebSocketService] WebSocket 구독 해제: ${symbol} ID: ${key}`);
    });

    // 주의: 채널 구독 해제는 하지 않음
    // 채널 구독/해제는 useWebSocketSubscription에서 관리합니다.
    // orderbook 채널은 계좌 변경 시에도 유지되어야 하므로 여기서 자동 해제하지 않습니다.
  }

  /**
   * 모든 구독 해제
   */
  unsubscribeAll(): void {
    if (!this.isConnected()) {
      console.warn('[WebSocketService] WebSocket 연결 없음 - 전체 구독 해제 실패');
      return;
    }

    const channels = Array.from(this.subscribedChannels.keys());
    if (channels.length === 0) {
      return;
    }

    const message: WebSocketSubscriptionMessage = {
      action: 'UNSUBSCRIBE',
      channels: channels,
      ...(this.accessToken && { token: `Bearer ${this.accessToken}` }),
    };

    this.webSocket!.send(JSON.stringify(message));

    // 모든 구독 해제
    this.subscriptions.clear();
    this.subscribedChannels.clear();
  }

  /**
   * 재연결 시도 (수동 호출)
   */
  async reconnect(): Promise<void> {
    // reconnect()가 동시에 여러 번 호출되어도, 진행 중인 재연결은 하나만 유지
    if (this.reconnectPromise) {
      return this.reconnectPromise;
    }
    // disconnect(false)에서 채널 목록이 초기화되므로 재연결 전에 스냅샷을 보관한다.
    const subscribedChannelsSnapshot = new Map(this.subscribedChannels);
    this.reconnectPromise = (async () => {
      // 재연결 경로이므로 자동 재연결을 켜고 의도적 종료(disconnect false)로 현재 소켓을 정리한다.
      this.getReconnectManager().setAutoReconnect(true);
      await this.disconnect(false);
      // 연결 복구 전에 채널 스냅샷을 복원해 onopen에서 정상 재구독되게 한다.
      this.subscribedChannels = subscribedChannelsSnapshot;
      this.autoReconnect = true;
      if (this.reconnectManager) {
        this.reconnectManager.setAutoReconnect(true);
      }
      await this.connect();
    })();

    try {
      await this.reconnectPromise;
    } finally {
      this.reconnectPromise = null;
    }
  }

  /**
   * 토큰 갱신·풀싱크 후 재연결 (앱 포그라운드 복귀 등)
   * ReconnectManager.attemptReconnect() 경로로 계좌 목록/JWT/데이터 초기화 후 연결한다.
   */
  async reconnectWithFullSync(): Promise<void> {
    if (this.reconnectPromise) {
      return this.reconnectPromise;
    }
    this.reconnectPromise = (async () => {
      const subscribedChannelsSnapshot = new Map(this.subscribedChannels);
      this.getReconnectManager().setAutoReconnect(true);
      await this.disconnect(false);
      this.subscribedChannels = subscribedChannelsSnapshot;
      this.autoReconnect = true;
      if (this.reconnectManager) {
        this.reconnectManager.setAutoReconnect(true);
      }
      await this.getReconnectManager().attemptReconnect({ force: true });
    })();

    try {
      await this.reconnectPromise;
    } finally {
      this.reconnectPromise = null;
    }
  }

  /**
   * 저장된 채널 재구독
   * 재연결 성공 시 호출됨
   * - market.quote, market.ticker, private: 토큰 채널을 한 번에 전송 (토큰/옵션 누락 방지)
   * - market.orderbook, market.orderbook.cancel: stockCd별로 한 번에 전송
   */
  private resubscribeStoredChannels(): void {
    const subscriptions = Array.from(this.subscribedChannels.entries());

    if (subscriptions.length === 0) {
      console.log('[WebSocketService] 재구독할 채널이 없습니다.');
      return;
    }

    const accountStore = useAccountStore();
    const hasAccount = !!accountStore.selectedAccountNo;

    // 1. 토큰 채널 배치: market.quote, market.ticker, (private) — 한 번에 전송
    const tokenChannelNames: WebSocketChannel[] = ['market.quote', 'market.ticker'];
    const tokenChannels = tokenChannelNames.filter((ch) => this.subscribedChannels.has(ch));
    if (hasAccount && this.subscribedChannels.has('private')) {
      tokenChannels.push('private');
    } else if (!hasAccount && this.subscribedChannels.has('private')) {
      this.subscribedChannels.delete('private');
    }

    if (tokenChannels.length > 0) {
      const token = this.accessToken ? `Bearer ${this.accessToken}` : undefined;
      this.subscribeToChannels(tokenChannels, token ? { token } : undefined, true);
    }

    // 2. orderbook 채널 배치: stockCd별로 한 번에 전송
    const orderbookByStockCd = new Map<string, WebSocketChannel[]>();
    for (const [channel, options] of subscriptions) {
      if (
        (channel === 'market.orderbook' || channel === 'market.orderbook.cancel') &&
        options?.stockCd
      ) {
        const list = orderbookByStockCd.get(options.stockCd) ?? [];
        list.push(channel);
        orderbookByStockCd.set(options.stockCd, list);
      } else if (
        (channel === 'market.orderbook' || channel === 'market.orderbook.cancel') &&
        !options?.stockCd
      ) {
        console.warn(`[WebSocketService] ${channel} 채널 재구독 실패: stockCd 없음`);
        this.subscribedChannels.delete(channel);
      }
    }

    for (const [stockCd, channels] of orderbookByStockCd) {
      this.subscribeToChannels(
        channels,
        {
          stockCd,
          ...(this.accessToken && { token: `Bearer ${this.accessToken}` }),
        },
        true
      );
    }

    // TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
    this.logQuoteData('reconnect');
  }

  /**
   * 정리 (리소스 해제)
   */
  cleanup(): void {
    // 정리 시에는 재연결하지 않음
    this.disconnect(false);
  }

  /**
   * 개발 모드에서 window 객체에 WebSocket 제어 메서드 노출
   */
  private exposeDevMethods(): void {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      (window as any).__ws = {
        disconnect: (allowAutoReconnect: boolean = true) => {
          console.log('[WebSocketService] WebSocket 연결 해제');
          this.disconnect(allowAutoReconnect);
        },
        reconnect: async () => {
          console.log('[WebSocketService] WebSocket 재연결');
          await this.reconnect();
        },
        getState: () => {
          return {
            connectionState: this.getConnectionState(),
            isConnected: this.isConnected(),
          };
        },
      };
    }
  }

  /**
   * 계좌 잔고 업데이트 (예수금과 증거금)
   */
  private updateAccountBalance(deposit: number, totalMargin: number): void {
    try {
      const assetStore = useAssetStore();
      assetStore.updateBalance(deposit, totalMargin);
    } catch (error) {
      console.error('[WebSocketService] 계좌 잔고 업데이트 오류:', error);
    }
  }

  /**
   * 바이너리 메시지 처리
   */
  private async handleBinaryMessage(data: ArrayBuffer | Blob): Promise<void> {
    try {
      // BinaryMessageDecoder로 디코딩
      // - Blob, ArrayBuffer 모두 처리
      const decoded =
        data instanceof Blob
          ? await BinaryMessageDecoder.decodeBlob(data)
          : BinaryMessageDecoder.decode(data);

      // console.log('[WebSocketService] 바이너리 메시지 디코딩:', {
      //   messageType: decoded.messageType,
      //   typeCode: decoded.typeCode,
      // });

      // 디코딩된 메시지를 타입별로 처리
      this.handleDecodedMessage(decoded);
    } catch (error) {
      console.error('[WebSocketService] 바이너리 메시지 처리 오류:', error);
    }
  }

  /**
   * 디코딩된 메시지 타입별 처리
   */
  /**
   * 디코딩된 메시지 타입별 처리
   */
  private handleDecodedMessage(decoded: any): void {
    const { messageType, data } = decoded;

    switch (messageType) {
      case 'MARKET_QUOTE_LIST':
        // 시장 시세 목록: 여러 종목의 매수/매도 가격 업데이트
        this.handleMarketQuoteListFromBinary(data);
        break;
      case 'MARKET_ORDER_BOOK':
        // 호가 장부: 특정 종목의 호가 정보(5단계) 업데이트
        this.handleMarketOrderBookFromBinary(data);
        break;
      case 'MARKET_ORDER_BOOK_CANCEL':
        // 호가 취소: QuoteCancelType에 따라 시세 초기화 (1: 특정 종목, 4: 모든 종목)
        this.handleOrderBookCancelFromBinary(data);
        break;
      case 'ORDER_ACCEPTED':
        // 주문 접수: 원주문의 조건부 주문(STOP, LIMIT_STOP) 및 TPSL에 대해 토스트 메시지 표시
        // 주문 탭/미체결 탭 업데이트 (원주문: 추가, 정정: 수정, 취소: 삭제/수정)
        this.handleOrderReceivedFromBinary(data);
        break;
      case 'ORDER_REJECTED':
        // 주문 거부: 조건부 주문(STOP, LIMIT_STOP) 및 TPSL에 대해 토스트 메시지 표시
        this.handleOrderRejectedFromBinary(data);
        break;
      case 'ORDER_EXECUTED':
        // 주문 체결: 모든 주문 유형에 대해 토스트 메시지 표시 및 계좌 잔고 업데이트
        this.handleOrderExecutedFromBinary(data);
        break;
      case 'ACCOUNT_BALANCE_UPDATE':
        // 잔고 업데이트: 포지션 잔고 정보 업데이트 (주문 체결 후 잔고 변경)
        this.handleBalanceUpdatedFromBinary(data);
        break;
      case 'DEPOSIT_UPDATED':
        // 예수금 업데이트: 계좌 예수금 정보 업데이트
        this.handleDepositUpdatedFromBinary(data);
        break;
      case 'MARKET_TICKER':
        // 시장 티커: OHLC 데이터 업데이트 (고가/저가, 전일 종가 등)
        this.handleMarketTickerFromBinary(data);
        break;
      case 'ORDER_CLOSED':
        // 청산 완료: 청산 내역에 추가
        this.handleOrderClosedFromBinary(data);
        break;
      default:
        console.warn('[WebSocketService] 알 수 없는 메시지 타입:', messageType);
    }
  }

  /**
   * 바이너리 MarketQuoteList 처리
   */
  private handleMarketQuoteListFromBinary(data: DecodedMarketQuoteList): void {
    const { quotes } = data;

    if (quotes && Array.isArray(quotes)) {
      // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // console.log('[WebSocketService] MarketQuoteList 바이너리 디코딩 성공!');
      // console.log('  총 개수:', quotes.length);
      quotes.forEach((quote, index) => {
        // console.log(`  [${index}]`, {
        //   종목코드: quote.stockCd,
        //   매수호가: quote.buy,
        //   매도호가: quote.sell,
        //   매수잔량: quote.buySize,
        //   매도잔량: quote.sellSize,
        //   타임스탬프: quote.timestamp,
        // });
        this.handleMarketQuoteFromBinary(quote);
      });
      // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
  }

  /**
   * 바이너리 MarketQuote 처리
   */
  private handleMarketQuoteFromBinary(quote: DecodedMarketQuote): void {
    const { stockCd, buy, sell, timestamp } = quote;

    if (!stockCd || buy === null || buy === undefined || sell === null || sell === undefined) {
      return;
    }

    // AUDCAD 종목 데이터 비교 로그 (테스트용)
    // if (stockCd === 'AUDCAD') {
    //   console.error('━━━━━━━━━━━━━━QUOTE━━━━━━━━━━━━━━━━');
    //   console.error('  매수호가:', buy);
    //   console.error('  매도호가:', sell);
    // }

    try {
      const marketDataStore = useMarketDataStore();

      // Pinia Store를 통한 시장 데이터 업데이트
      marketDataStore.updateMarketDataFromStream(stockCd, {
        buyPrice: buy,
        sellPrice: sell,
        timestamp: timestamp as number,
      });

      // Event Bus를 통한 이벤트 발생
      globalEventBus.emit(MARKET_EVENTS.DATA_UPDATED, {
        symbol: stockCd,
        buyPrice: buy,
        sellPrice: sell,
        timestamp: timestamp,
      });
    } catch (error) {
      console.error('[WebSocketService] MarketQuote 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 MarketOrderBook 처리
   */
  private handleMarketOrderBookFromBinary(data: DecodedMarketOrderBook): void {
    const { stockCd, quotes, timestamp } = data;

    if (!stockCd || !quotes || !Array.isArray(quotes)) {
      return;
    }

    // AUDCAD 종목 데이터 비교 로그 (테스트용)
    // if (stockCd === 'AUDCAD' && quotes.length > 0) {
    //   const firstQuote = quotes[0]; // 1호가
    //   console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    //   console.error('  매수호가(1호가):', firstQuote.buy);
    //   console.error('  매도호가(1호가):', firstQuote.sell);
    // }

    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('[WebSocketService] MarketOrderBook 바이너리 디코딩 성공!');
    // console.log('  종목코드:', stockCd);
    // console.log('  호가 개수:', quotes.length);
    // console.log('  타임스탬프:', timestamp);
    // console.log('  호가 정보:');
    // quotes.forEach((quote, index) => {
    //   console.log(`    [단계 ${index + 1}]`, {
    //     매수호가: quote.buy,
    //     매도호가: quote.sell,
    //     매수잔량: quote.buySize,
    //     매도잔량: quote.sellSize,
    //   });
    // });
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // 호가 데이터 변환 (proto에서 number로 정의된 필드를 정확한 문자열로 변환)
    const orderBookData = quotes.map((quote) => ({
      buy: toDecimalString(quote.buy),
      sell: toDecimalString(quote.sell),
      buySize: toDecimalString(quote.buySize),
      sellSize: toDecimalString(quote.sellSize),
    }));

    // 전역 이벤트로 호가 데이터 전달
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('orderBookUpdate', {
          detail: {
            stockCd: stockCd,
            orderBook: orderBookData,
            timestamp: timestamp,
          },
        })
      );
    }
  }

  /**
   * 바이너리 MarketTicker 처리
   * OHLC 데이터를 받아 시장 데이터 스토어를 업데이트합니다.
   *
   * 처리 내용:
   * 1. 고가/저가 업데이트: marketDataStore의 high/low 필드 업데이트
   * 2. 전일 종가 업데이트: metadataStore의 closePrice 필드 업데이트 (beforeClose 사용)
   *
   * @param data - MarketTicker 메시지 데이터
   * @param data.stockCd - 종목 코드
   * @param data.high - 고가
   * @param data.low - 저가
   * @param data.beforeClose - 전일 종가
   */
  private handleMarketTickerFromBinary(data: DecodedMarketTicker): void {
    try {
      const marketDataStore = useMarketDataStore();
      const { stockCd, high, low, beforeClose } = data;

      if (!stockCd) {
        return;
      }

      // 고가/저가 업데이트
      if (high > 0 || low > 0) {
        marketDataStore.setDailyHighLow(stockCd, high, low);
      }

      // 전일 종가 업데이트 (beforeClose 사용)
      if (beforeClose > 0) {
        const metadataStore = useMetadataStore();
        metadataStore.updateClosePrice(stockCd, beforeClose);
      }

      // 전역 이벤트 발생 (필요한 경우)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('marketTickerUpdate', {
            detail: {
              ...data,
            },
          })
        );
      }
    } catch (error) {
      console.error('[WebSocketService] MarketTicker 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 OrderBookCancel 처리
   * 호가 취소 메시지를 받아 QuoteCancelType에 따라 시세를 초기화합니다.
   *
   * 처리 규칙:
   * - QuoteCancelType 4: 모든 Symbol의 모든 시세 삭제 (시세 데이터를 초기값으로 리셋, 전일종가는 유지)
   * - QuoteCancelType 1: 특정 Symbol의 모든 시세 삭제 (해당 종목의 시세 데이터를 초기값으로 리셋, 전일종가는 유지)
   * - 기타 타입: 처리하지 않음
   *
   * @param data - OrderBookCancel 메시지 데이터
   * @param data.cancelType - 취소 타입 ('1': 특정 종목, '4': 모든 종목)
   * @param data.stockCd - 종목 코드 (cancelType이 '1'일 때 필수)
   * @param data.timestamp - 타임스탬프
   *
   * @example
   * // 모든 종목 시세 초기화
   * handleOrderBookCancelFromBinary({ cancelType: '4', stockCd: '', timestamp: 1234567890 })
   *
   * // 특정 종목 시세 초기화
   * handleOrderBookCancelFromBinary({ cancelType: '1', stockCd: 'EURUSD', timestamp: 1234567890 })
   */
  private handleOrderBookCancelFromBinary(data: IOrderBookCancel): void {
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('[WebSocketService] OrderBookCancel 바이너리 디코딩 성공!');
    // console.log('  취소타입:', data.cancelType);
    // console.log('  종목코드:', data.stockCd);
    // console.log('  타임스탬프:', data.timestamp);
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    try {
      const marketDataStore = useMarketDataStore();
      const cancelType = String(data.cancelType || '').trim();

      // QuoteCancelType에 따른 처리
      switch (cancelType) {
        case '1': {
          // 특정 Symbol의 모든 시세 삭제
          const stockCd = data.stockCd;
          if (!stockCd) {
            return;
          }
          marketDataStore.clearSymbolMarketData(stockCd);

          break;
        }
        case '4': {
          // 모든 Symbol의 모든 시세 삭제
          marketDataStore.clearAllMarketData();
          break;
        }
        default: {
          break;
        }
      }
    } catch (error) {
      console.error('[WebSocketService] OrderBookCancel 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 OrderReceived 처리 (주문 접수)
   * 주문 접수 메시지를 받아 토스트 메시지를 표시하고 주문 탭/미체결 탭을 업데이트합니다.
   *
   * 처리 내용:
   * 1. 토스트 메시지 표시
   *    - 원주문(ORIGINAL) 접수 시 시장가/지정가/조건부(MARKET, LIMIT, STOP, LIMIT_STOP) 주문 완료 토스트 표시
   *    - 원주문(ORIGINAL)의 TPSL 발동 시 토스트 표시
   *    - 정정(MODIFY), 취소(CANCEL) 접수 시 각각 토스트 표시
   *
   * 2. 주문 탭/미체결 탭 업데이트 (전역 이벤트 'orderAccepted' 발생)
   *    - 원주문(ORIGINAL): 주문 탭에 주문 추가, 미체결 탭에 미체결 추가
   *    - 정정(MODIFY): 주문 탭/미체결 탭에서 해당 주문 수정 (배리어가격, 주문가격, 이익실현배리어가격, 손실제한배리어가격)
   *    - 취소(CANCEL): 주문 탭에서 주문잔량=0, 주문상태=CANCELLED로 수정, 미체결 탭에서 주문 삭제
   *
   * 3. 원주문 접수 시 청산가능 수량 조정 (청산 주문인 경우만)
   *    - 청산 판단: LONG + SELL_TO_CLOSE 또는 SHORT + BUY_TO_COVER
   *    - 청산가능 = 청산가능 - 주문수량
   *    - SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호
   *
   * @param data - OrderReceived 메시지 데이터
   * @param data.orderVersionCd - 주문 버전 코드 (ORIGINAL: 원주문, MODIFY: 정정, CANCEL: 취소)
   * @param data.orderTypeCd - 주문 유형 코드 (MARKET, LIMIT, STOP, LIMIT_STOP, TPSL)
   * @param data.stockCd - 종목 코드
   * @param data.orderNo - 주문번호
   * @param data.orderDate - 주문일자
   * @param data.accountNo - 계좌번호
   * @param data.positionCd - 포지션 구분 코드 (LONG, SHORT)
   * @param data.sideCd - 매매 구분 코드 (BUY, SELL, PURCHASE, SELL_TO_CLOSE, BUY_TO_COVER)
   * @param data.orderQuantity - 주문수량
   * @param data.orderPrice - 주문가격
   * @param data.orderBalance - 주문잔량
   * @param data.orderStatusCd - 주문상태 코드
   * @param data.barrierPrice - 배리어가격
   * @param data.profitRealizationBarrierPrice - 이익실현배리어가격
   * @param data.lossCutBarrierPrice - 손실제한배리어가격
   */
  private handleOrderReceivedFromBinary(data: DecodedOrderReceived): void {
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('[WebSocketService] OrderReceived (주문 접수) 바이너리 디코딩 성공!');
    // console.log('  계좌번호:', data.accountNo);
    // console.log('  주문일시:', data.orderDateTime);
    // console.log('  주문번호:', data.orderNo);
    // console.log('  종목코드:', data.stockCd);
    // console.log('  주문수량:', data.orderQuantity);
    // console.log('  주문가격:', data.orderPrice);
    // console.log('  주문잔량:', data.orderBalance);
    // console.log('  주문상태:', data.orderStatusCd);
    // console.log('  포지션구분:', data.positionCd, '(LONG, SHORT)');
    // console.log('  매매구분:', data.sideCd);
    // console.log('  주문버전:', data.orderVersionCd);
    // console.log('  주문유형:', data.orderTypeCd, '(MARKET, LIMIT, STOP, LIMIT_STOP, TPSL)');
    // console.log('  증거금총액:', data.totalMargin);
    // console.log('  매입일시:', data.purchaseDateTime);
    // console.log('  잔고번호:', data.balanceNo);
    // console.log('  티커메시지여부:', data.isTickerMessage);
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    try {
      const toastStore = useToastStore();
      const stockCd = data.stockCd || '';

      // 원주문(ORIGINAL) 접수 시 시장가/지정가/조건부 주문에 대해 주문 완료 토스트 표시
      if (data.orderVersionCd === 'ORIGINAL' && stockCd) {
        const orderTypeCd = data.orderTypeCd;
        if (
          orderTypeCd === 'MARKET' ||
          orderTypeCd === 'LIMIT' ||
          orderTypeCd === 'STOP' ||
          orderTypeCd === 'LIMIT_STOP'
        ) {
          const sideLabel = this.getSideLabelFromPositionCd(data.positionCd);
          const pricePart =
            orderTypeCd === 'LIMIT' && data.orderPrice != null ? `, ${data.orderPrice}` : '';
          toastStore.addOrderCompleteToast(
            EnumLabelMapper.getOrderTypeLabel(orderTypeCd),
            `${stockCd} (${sideLabel}, ${data.orderQuantity} 랏${pricePart})`
          );
        }
      }

      // 티커메시지여부가 'Y'이고 원주문(ORIGINAL)이며 TPSL인 경우에만 토스트 메시지 표시
      if (
        data.isTickerMessage === 'Y'
        // data.orderVersionCd === 'ORIGINAL' &&
        // data.orderTypeCd === 'TPSL'
      ) {
        toastStore.addOrderTriggeredToast(`${stockCd} SL/TP 주문이 발동되었어요.`);
      }

      // 정정(MODIFY) 주문 접수 시 토스트 메시지 표시
      if (data.orderVersionCd === 'MODIFY') {
        const sideLabel = this.getSideLabelFromPositionCd(data.positionCd);
        toastStore.addOrderModifiedToast(`${stockCd} (${sideLabel}, ${data.orderQuantity} 랏)`);
      }

      // 취소(CANCEL) 주문 접수 시 토스트 메시지 표시
      if (data.orderVersionCd === 'CANCEL') {
        const sideLabel = this.getSideLabelFromPositionCd(data.positionCd);
        toastStore.addOrderCancelledToast(`${stockCd} (${sideLabel}, ${data.orderQuantity} 랏)`);
      }

      // orderTypeCd를 priceType으로 변환 (미체결용)
      const priceType =
        data.orderTypeCd === 'MARKET' || data.orderTypeCd === 'STOP' || data.orderTypeCd === 'TPSL'
          ? 'MARKET'
          : data.orderTypeCd === 'LIMIT' || data.orderTypeCd === 'LIMIT_STOP'
            ? 'LIMIT'
            : 'MARKET';

      const pendingOrderData = {
        orderDateTime: parseDateTimeString(data.orderDateTime),
        orderNo: data.orderNo,
        stockCd: data.stockCd,
        positionCd: data.positionCd,
        orderTypeCd: data.orderTypeCd,
        orderCd: data.orderCd,
        priceType: priceType,
        orderQuantity: data.orderQuantity,
        barrierPrice: data.barrierPrice,
        orderPrice: data.orderPrice,
        profitRealizationBarrierPrice: data.profitRealizationBarrierPrice,
        lossCutBarrierPrice: data.lossCutBarrierPrice,
        orderBalance: data.orderBalance,
        orderStatusCd: data.orderStatusCd,
        isProfitExecution: data.isProfitExecution || '',
        isLossLimits: data.isLossLimits || '',
        isLossTracing: data.isLossTracing || '',
        purchasePrice: data.purchasePrice,
        orderVersionCd: data.orderVersionCd || '',
        // Trailing Stop: 최초손실추적가격, 최초손실제한가격 (주문시점의 고정값)
        firstLossTrackingPrice:
          (data as any).firstLossTrackingPrice && (data as any).firstLossTrackingPrice !== ''
            ? parseFloat((data as any).firstLossTrackingPrice)
            : 0,
        firstLossCutPrice:
          (data as any).firstLossCutPrice && (data as any).firstLossCutPrice !== ''
            ? parseFloat((data as any).firstLossCutPrice)
            : 0,
      };

      // 원주문 접수 시 청산가능 수량 조정 (청산 주문인 경우만)
      // 청산 판단: orderCd가 CLOSE_OUT인 경우
      if (data.orderVersionCd === 'ORIGINAL') {
        if (data.orderCd === 'CLOSE_OUT' && data.purchaseDateTime && data.balanceNo) {
          // 청산가능 수량 조정: 청산가능 = 청산가능 - 주문수량
          const orderDataStore = useOrderDataStore();
          const positionStocks = orderDataStore.positionStocks;
          const convertedPurchaseDateTime = parseDateTimeString(data.purchaseDateTime);

          const existingPosition = positionStocks.find(
            (position) =>
              position.stockCd === data.stockCd &&
              position.positionCd === data.positionCd &&
              position.purchaseDateTime === convertedPurchaseDateTime &&
              position.positionNo === data.balanceNo
          );

          if (existingPosition) {
            const newLiquidationPossibleQuantity =
              existingPosition.liquidationPossibleQuantity - data.orderQuantity;
            // purchaseDateTime과 balanceNo를 함께 전달하여 정확한 잔고를 찾아 업데이트
            orderDataStore.updatePositionStock(
              data.stockCd,
              data.positionCd,
              {
                liquidationPossibleQuantity: Math.max(0, newLiquidationPossibleQuantity),
              },
              convertedPurchaseDateTime,
              data.balanceNo
            );
          }
        }
      }

      // 취소 주문 접수 시 청산가능 수량 복구 (청산 취소 주문인 경우만)
      // 잔고에서 stockCd, positionCd, purchaseDateTime으로 직접 찾아서 수량 복구
      if (data.orderVersionCd === 'CANCEL' && data.purchaseDateTime) {
        const orderDataStore = useOrderDataStore();
        const positionStocks = orderDataStore.positionStocks;
        const convertedPurchaseDateTime = parseDateTimeString(data.purchaseDateTime);

        // 잔고에서 stockCd, positionCd, purchaseDateTime으로 주문 찾기
        const existingPosition = positionStocks.find(
          (position) =>
            position.stockCd === data.stockCd &&
            position.positionCd === data.positionCd &&
            position.purchaseDateTime === convertedPurchaseDateTime &&
            position.positionNo === data.balanceNo
        );

        if (existingPosition) {
          // 청산가능 수량 복구: 청산가능 = 청산가능 + 취소된 주문수량
          const newLiquidationPossibleQuantity =
            existingPosition.liquidationPossibleQuantity + data.orderQuantity;
          // purchaseDateTime으로 정확한 잔고를 찾아 업데이트
          orderDataStore.updatePositionStock(
            data.stockCd,
            data.positionCd,
            {
              liquidationPossibleQuantity: newLiquidationPossibleQuantity,
            },
            convertedPurchaseDateTime
          );
        }
      }

      // 계좌 잔고 업데이트 (예탁자산 업데이트)
      if (data.depositAssets !== undefined && data.totalMargin !== undefined) {
        this.updateAccountBalance(data.depositAssets, data.totalMargin);
      }

      // 전역 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('orderAccepted', {
            detail: {
              type: 'ORDER_ACCEPTED',
              data: pendingOrderData,
              accountNo: data.accountNo,
            },
          })
        );
      }
    } catch (error) {
      console.error('[WebSocketService] OrderReceived 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 OrderRejected 처리 (주문 거부)
   * 주문 거부 메시지를 받아 토스트 메시지를 표시하고 주문 탭/미체결 탭을 업데이트합니다.
   *
   * 처리 내용:
   * 1. 토스트 메시지 표시
   *    - 모든 주문 거부에 대해 토스트 메시지 표시
   *
   * 2. 주문 탭/미체결 탭 업데이트 (전역 이벤트 'orderRejected' 발생)
   *    - 주문 탭: 주문잔량=0, 주문상태=REJECTED로 수정 (SELECT KEY: 주문일자, 주문번호)
   *    - 미체결 탭: 미체결 삭제 (SELECT KEY: 주문일자, 주문번호)
   *    - 처리 위치: useOrderHistoryData.ts, usePendingOrderData.ts
   *
   * 3. 청산가능 수량 복구 (청산 주문인 경우만)
   *    - 청산 판단: 포지션구분코드=1(LONG) and 매매구분코드=2(SELL_TO_CLOSE)
   *                 또는 포지션구분코드=2(SHORT) and 매매구분코드=1(BUY_TO_COVER)
   *    - 청산가능 = 청산가능 + 거부수량
   *    - SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호
   *    - 처리 위치: usePositionData.ts (전역 이벤트 'orderRejected' 수신)
   *
   * @param data - OrderRejected 메시지 데이터
   * @param data.orderNo - 주문번호
   * @param data.stockCd - 종목 코드
   * @param data.accountNo - 계좌번호
   * @param data.orderDateTime - 주문일시
   * @param data.rejectQuantity - 거부수량
   * @param data.positionCd - 포지션 구분 코드 (LONG, SHORT)
   * @param data.sideCd - 매매 구분 코드 (BUY, SELL, PURCHASE, SELL_TO_CLOSE, BUY_TO_COVER)
   * @param data.purchaseDateTime - 매입일시 (청산가능 수량 복구용)
   * @param data.balanceNo - 잔고번호 (청산가능 수량 복구용)
   */
  private handleOrderRejectedFromBinary(data: DecodedOrderRejected): void {
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('[WebSocketService] OrderRejected (주문 거부) 바이너리 디코딩 성공!');
    // console.log('  계좌번호:', data.accountNo);
    // console.log('  주문일시:', data.orderDateTime);
    // console.log('  주문번호:', data.orderNo);
    // console.log('  종목코드:', data.stockCd);
    // console.log('  거부수량:', data.rejectQuantity);
    // console.log('  포지션구분:', data.positionCd, '(LONG, SHORT)');
    // console.log('  매매구분:', data.sideCd);
    // console.log('  매입일시:', data.purchaseDateTime);
    // console.log('  잔고번호:', data.balanceNo);
    // console.log('  증거금총액:', data.totalMargin);
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    try {
      // 토스트 메시지 표시
      const toastStore = useToastStore();
      const sideLabel = this.getSideLabelFromPositionCd(data.positionCd);
      toastStore.addOrderRejectedToast(
        `${data.stockCd || ''} (${sideLabel}, ${data.rejectQuantity} 랏)`
      );

      const rejectedData = {
        orderDateTime: parseDateTimeString(data.orderDateTime!),
        orderNo: data.orderNo!,
        stockCd: data.stockCd!,
        positionCd: data.positionCd!,
        sideCd: data.sideCd!,
        rejectQuantity: data.rejectQuantity!,
        purchaseDateTime: data.purchaseDateTime ? parseDateTimeString(data.purchaseDateTime) : '',
        balanceNo: data.balanceNo!,
        depositAssets: data.depositAssets!,
        totalMargin: data.totalMargin!,
        accountNo: data.accountNo!,
        orderCd: data.orderCd!,
      };

      // 계좌 잔고 업데이트 (예탁자산 업데이트)
      if (data.depositAssets !== undefined && data.totalMargin !== undefined) {
        this.updateAccountBalance(data.depositAssets, data.totalMargin);
      }

      // 전역 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('orderRejected', {
            detail: {
              type: 'ORDER_REJECTED',
              data: rejectedData,
              accountNo: data.accountNo,
            },
          })
        );
      }
    } catch (error) {
      console.error('[WebSocketService] OrderRejected 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 OrderExecuted 처리 (주문 체결)
   *
   * 처리 내용:
   * 1. 토스트 메시지 표시
   * 2. 계좌 잔고 업데이트
   * 3. 청산 주문인 경우 청산 탭에 추가 (청산 완료된 것만 표시)
   *    - 청산 탭에는 청산 완료된 것들만 표시되므로, 주문 접수 시점이 아닌 체결 시점에 추가
   *    - 주문 내역에서 orderCd === 'CLOSE_OUT'인 경우에만 청산 탭에 추가
   */
  private handleOrderExecutedFromBinary(data: DecodedOrderExecuted): void {
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('[WebSocketService] OrderExecuted (주문 체결) 바이너리 디코딩 성공!');
    // console.log('  계좌번호:', data.accountNo);
    // console.log('  주문일시:', data.orderDateTime);
    // console.log('  주문번호:', data.orderNo);
    // console.log('  체결수량:', data.executionQuantity);
    // console.log('  체결가격:', data.executionPrice);
    // console.log('  예수금:', data.depositAssets);
    // console.log('  증거금총액:', data.totalMargin);
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    try {
      // 주문 내역에서 종목 코드 및 매수/매도 정보 가져오기
      const orderDataStore = useOrderDataStore();
      const orderHistory = orderDataStore.orderHistory;
      const existingOrder = orderHistory.find((order) => order.orderNo === data.orderNo);
      const stockCd = existingOrder?.stockCd || '';
      const sideLabel = this.getSideLabelFromPositionCd(existingOrder?.positionCd);

      // 토스트 메시지 표시
      if (stockCd) {
        const toastStore = useToastStore();
        toastStore.addExecutionCompleteToast(
          `${stockCd} (${sideLabel}, ${data.executionQuantity} 랏, ${data.executionPrice})`
        );
      }

      const executionData = {
        orderDateTime: parseDateTimeString(data.orderDateTime!),
        orderNo: data.orderNo!,
        executionQuantity: data.executionQuantity!,
        executionPrice: data.executionPrice!,
        executionDateTime: parseDateTimeString(data.orderDateTime!),
        deposit: data.depositAssets!,
        totalMargin: data.totalMargin!,
        accountNo: data.accountNo!,
      };

      // 계좌 잔고 업데이트
      this.updateAccountBalance(data.depositAssets!, data.totalMargin!);

      // 전역 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('orderExecution', {
            detail: {
              type: 'ORDER_EXECUTION',
              data: executionData,
              accountNo: data.accountNo,
            },
          })
        );
      }
    } catch (error) {
      console.error('[WebSocketService] OrderExecuted 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 BalanceUpdated 처리 (잔고 업데이트)
   * 잔고 업데이트 메시지를 받아 잔고 탭을 업데이트합니다.
   *
   * 처리 내용:
   * - 장부수량 > 0: 잔고 수정 또는 생성 (SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호)
   * - 장부수량 = 0: 잔고 삭제 (SELECT KEY: 종목코드, 포지션구분코드, 매입일자, 잔고번호)
   *
   * @param data - BalanceUpdated 메시지 데이터
   * @param data.accountNo - 계좌번호
   * @param data.orderDate - 주문일자
   * @param data.orderNo - 주문번호
   * @param data.stockCd - 종목 코드
   * @param data.tradeCurrencyCd - 거래통화 코드
   * @param data.positionCd - 포지션 구분 코드 (LONG, SHORT)
   * @param data.purchaseDate - 매입일자
   * @param data.bookQuantity - 장부수량
   * @param data.liquidationPossibleQuantity - 청산가능수량
   * @param data.bookPrice - 장부가격
   * @param data.currentPrice - 현재가
   * @param data.evaluationProfitLoss - 평가손익
   * @param data.stockGroupCd - 종목그룹 코드
   * @param data.balanceNo - 잔고번호
   */
  private handleBalanceUpdatedFromBinary(data: DecodedBalanceUpdated): void {
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    // console.log('[WebSocketService] BalanceUpdated (잔고 수정) 바이너리 디코딩 성공!');
    // console.log('  계좌번호:', data.accountNo);
    // console.log('  주문일시:', data.orderDateTime);
    // console.log('  주문번호:', data.orderNo);
    // console.log('  종목코드:', data.stockCd);
    // console.log('  거래통화:', data.tradeCurrencyCd);
    // console.log('  포지션구분:', data.positionCd, '(LONG, SHORT)');
    // console.log('  매입일시:', data.purchaseDateTime);
    // console.log('  장부수량:', data.bookQuantity);
    // console.log('  청산가능수량:', data.liquidationPossibleQuantity);
    // console.log('  장부가격:', data.bookPrice);
    // console.log('  현재가:', data.currentPrice);
    // console.log('  평가손익:', data.evaluationProfitLoss);
    // console.log('  종목그룹:', data.stockGroupCd, '(01:외환, 02:지수, 03:상품, 04:가상화폐)');
    // console.log('  잔고번호:', data.balanceNo);
    // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    try {
      const balanceData = {
        orderDateTime: parseDateTimeString(data.orderDateTime!),
        orderNo: data.orderNo!,
        stockCd: data.stockCd!,
        tradeCurrencyCd: data.tradeCurrencyCd!,
        positionCd: data.positionCd!,
        purchaseDateTime: parseDateTimeString(data.purchaseDateTime!),
        accountBookQuantity: data.bookQuantity!,
        liquidationPossibleQuantity: data.liquidationPossibleQuantity!,
        accountBookPrice: data.bookPrice!,
        currentPrice: data.currentPrice!,
        assessmentProfitLoss: data.evaluationProfitLoss!,
        stockGroupCd: data.stockGroupCd!,
        positionNo: data.balanceNo!,
      };

      // 전역 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('accountBalance', {
            detail: {
              type: 'BALANCE_UPDATED',
              data: balanceData,
              accountNo: data.accountNo,
            },
          })
        );
      }
    } catch (error) {
      console.error('[WebSocketService] BalanceUpdated 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 DepositUpdated 처리 (예수금 업데이트)
   * 예수금 업데이트 메시지를 받아 화면 상단의 예탁자산을 업데이트합니다.
   *
   * 처리 내용:
   * - 입금 확인 또는 출금 확인 등 예수금 변동이 생겼을 경우 화면 상단의 예탁자산 변경
   * - 선택된 계좌의 예수금만 업데이트
   *
   * @param data - DepositUpdated 메시지 데이터
   * @param data.accountNo - 계좌번호
   * @param data.depositAssets - 예수금(예탁자산) 금액
   */
  private handleDepositUpdatedFromBinary(data: DecodedDepositUpdated): void {
    try {
      // AccountStore에 예수금 업데이트
      const accountStore = useAccountStore();
      const assetStore = useAssetStore();

      if (accountStore.selectedAccountNo === data.accountNo) {
        // depositAssets를 depositBalance로 설정
        assetStore.setDepositBalance(data.depositAssets!);
      }

      // 전역 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('depositUpdated', {
            detail: {
              type: 'DEPOSIT_UPDATED',
              data: {
                accountNo: data.accountNo,
                depositAmount: data.depositAssets,
              },
              accountNo: data.accountNo,
            },
          })
        );
      }
    } catch (error) {
      console.error('[RealWebSocketService] DepositUpdated 바이너리 처리 오류:', error);
    }
  }

  /**
   * 바이너리 OrderClosed 처리 (청산 완료)
   * 청산 완료 메시지를 받아 청산 내역에 추가합니다.
   *
   * 처리 내용:
   * 1. 전역 이벤트 'orderClosed' 발생
   * 2. useCloseOutData에서 이벤트를 수신하여 청산 내역에 추가
   *
   * @param data - OrderClosed 메시지 데이터
   */
  private handleOrderClosedFromBinary(data: DecodedOrderClosed): void {
    console.error(data);
    try {
      // DetailsCloseOutOrder 형식으로 변환
      const closeOutData = {
        purchaseDateTime: parseDateTimeString(data.purchaseDateTime),
        orderNo: data.orderNo,
        executionDateTime: parseDateTimeString(data.executionDateTime),
        stockCd: data.stockCd,
        tradeCurrencyCd: data.tradeCurrencyCd,
        positionCd: data.positionCd as 'LONG' | 'SHORT' | 'TOTAL',
        closeOutQuantity: data.closeOutQuantity,
        purchasePrice: data.purchasePrice,
        closeOutPrice: data.closeOutPrice,
        closeOutProfitLoss: data.closeOutProfitLoss,
        executionFee: data.executionCharge,
        swapFee: data.swapCharge,
      };

      // 전역 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('orderClosed', {
            detail: {
              type: 'ORDER_CLOSED',
              data: closeOutData,
              accountNo: data.accountNo,
            },
          })
        );
      }
    } catch (error) {
      console.error('[WebSocketService] OrderClosed 바이너리 처리 오류:', error);
    }
  }

  /**
   * 시세 디버그 로그를 출력할지 여부 (개발 환경에서만 true)
   */
  private isQuoteDebugLogEnabled(): boolean {
    return import.meta.env.DEV;
  }

  /**
   * TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
   * 5초마다 시세 정보를 로그로 출력합니다.
   * 계좌번호, 종목코드, 스프레드등급, 매수시세, 매도시세, 종목스프레드 사용여부, 매수 스프레드, 매도 스프레드를 출력합니다.
   */
  private startPeriodicQuoteLog(): void {
    if (!this.isQuoteDebugLogEnabled()) {
      return;
    }
    // 이미 실행 중이면 중복 실행 방지
    if (this.quoteLogIntervalId) {
      return;
    }

    // 5초마다 시세 정보 출력
    this.quoteLogIntervalId = setInterval(() => {
      this.logQuoteData('periodic');
    }, 5000);
  }

  /**
   * TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
   * 주기적 시세 로그 출력을 중지합니다.
   */
  private stopPeriodicQuoteLog(): void {
    if (this.quoteLogIntervalId) {
      clearInterval(this.quoteLogIntervalId);
      this.quoteLogIntervalId = null;
    }
  }

  /**
   * TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
   * 현재 선택된 계좌와 종목의 시세 정보를 로그로 출력합니다.
   * 외부에서도 호출 가능하도록 public으로 선언 (계좌 변경 시점 등에서 사용)
   * @param source - 로그 출력 출처 ('periodic' | 'accountChange' | 'symbolChange' | 'reconnect')
   */
  logQuoteData(
    source: 'periodic' | 'accountChange' | 'symbolChange' | 'reconnect' = 'periodic'
  ): void {
    if (!this.isQuoteDebugLogEnabled()) {
      return;
    }
    try {
      const accountStore = useAccountStore();
      const accountNo = accountStore.selectedAccountNo;

      if (!accountNo) {
        return;
      }

      // 계좌 정보 조회
      const account = accountStore.accountListComputed.find((acc) => acc.accountNo === accountNo);
      const accountGrade = account?.accountGrade || '-';

      // 선택된 종목 정보 조회
      const selectedSymbolStore = useSelectedSymbolStore();
      const stockCd = selectedSymbolStore.selectedSymbol;

      if (!stockCd) {
        return;
      }

      // 종목 메타데이터 조회
      const metadataStore = useMetadataStore();
      const symbolMetadata = metadataStore.symbolMetadata.get(stockCd);

      // 시세 정보 조회
      const marketDataStore = useMarketDataStore();
      const symbolData = marketDataStore.getSymbolDataBySymbol(stockCd);

      const buyPrice = symbolData?.buyPrice ?? 0;
      const sellPrice = symbolData?.sellPrice ?? 0;
      const spread = subtract(buyPrice, sellPrice); // 스프레드 (매수가격 - 매도가격, decimal.js로 정확한 계산)
      const isSpreadUsage = symbolMetadata?.isSpreadUsage ? 'Y' : 'N';
      const buySpread = symbolMetadata?.buySpread ?? 0;
      const sellSpread = symbolMetadata?.sellSpread ?? 0;

      // 객체를 항상 펼쳐서 볼 수 있도록 각 속성을 개별 로그로 출력
      const sourceLabel = {
        periodic: '주기적 출력',
        accountChange: '계좌 변경',
        symbolChange: '종목 변경',
        reconnect: '웹소켓 재구독',
      }[source];

      console.group(`[DEBUG] 시세 출력 (${sourceLabel})`);
      console.warn('계좌번호:', accountNo);
      console.warn('종목코드:', stockCd);
      console.warn('스프레드등급(계좌등급):', accountGrade);
      console.warn('매수시세:', buyPrice);
      console.warn('매도시세:', sellPrice);
      console.warn('종목스프레드 사용여부 (Y: 종목스프레드, N: 계좌스프레드):', isSpreadUsage);
      console.warn('ㄴ 스프레드 (매수가격 - 매도가격):', spread);
      console.warn('ㄴ 종목 매수 스프레드:', buySpread);
      console.warn('ㄴ 종목 매도 스프레드:', sellSpread);
      console.groupEnd();
    } catch (error) {
      console.error('[WebSocketService] 시세 주기 로그 출력 실패:', error);
    }
  }
}
